import {
	ComboBoxControlError,
	ComboBoxIntermediateState,
	SelectedComboBoxItem
} from "client/Components";
import { NewValueHandler } from "./misc";
import { IValueReducer } from "../reducer";
import {
	IInputValidation,
	IValueValidation,
	InputValidationResultKind
} from "client/Validation/input";
import { ensureNotNil, isNotNil } from "client/Misc/utils";

export interface IAttachableComboBoxProps<TInputValue> {
	value: SelectedComboBoxItem<TInputValue> | undefined;
	onChange(value: SelectedComboBoxItem<TInputValue> | undefined): void;
	trackingId: string;
	isInvalid: boolean;
	onErrorChange(error: ComboBoxControlError | undefined): void;
}

export interface IAttachableComboBoxPropsWithBuilder<TInputValue>
	extends IAttachableComboBoxProps<TInputValue> {

	withOnChange(
		handler: (newValue: SelectedComboBoxItem<TInputValue> | undefined) => void
	): IAttachableComboBoxPropsWithBuilder<TInputValue>;
}

export class AttachableComboBoxPropsWithBuilder<TInputValue>
	implements IAttachableComboBoxPropsWithBuilder<TInputValue> {

	private readonly _reducer: IValueReducer<TInputValue>;
	private readonly _extraOnChangeHandlers: NewValueHandler<SelectedComboBoxItem<TInputValue> | undefined>[];
	private readonly _attachableHandler: ComboBoxIntermediateState<TInputValue>;
	private readonly _defaultValue: TInputValue;
	private readonly _areEqual: (value1: TInputValue, value2: TInputValue) => boolean;

	public readonly value: SelectedComboBoxItem<TInputValue> | undefined;
	public readonly isInvalid: boolean;
	public readonly validation: IInputValidation | undefined;
	public readonly trackingId: string;

	constructor (
		reducer: IValueReducer<TInputValue>,
		validation: IValueValidation<TInputValue> | undefined,
		attachableHandler: ComboBoxIntermediateState<TInputValue>,
		defaultValue: TInputValue,
		areEqual: ((value1: TInputValue, value2: TInputValue) => boolean) | undefined
	) {
		this._reducer = reducer;
		this.validation = validation;
		this._attachableHandler = attachableHandler;
		this._extraOnChangeHandlers = [];
		this._defaultValue = defaultValue;
		this._areEqual = areEqual ?? ((a, b) => a === b);

		this.ensureSelectedItemMatchesTheValue();

		this.value = this._areEqual(reducer.value, this._defaultValue)
			? undefined
			: ensureNotNil(this._attachableHandler.selectedItem);

		this.isInvalid = this.validation?.isValid() === false;
		this.validation = validation;
		this.trackingId = reducer.path;

		this.onChange = this.onChange.bind(this);
		this.withOnChange = this.withOnChange.bind(this);
		this.onErrorChange = this.onErrorChange.bind(this);
	}

	public withOnChange(
		handler: (newValue: SelectedComboBoxItem<TInputValue> | undefined) => void
	): IAttachableComboBoxPropsWithBuilder<TInputValue> {
		this._extraOnChangeHandlers.push(handler);
		return this;
	}

	public onChange(newValue: SelectedComboBoxItem<TInputValue> | undefined): void {
		const {
			onSelectedItemChange
		} = this._attachableHandler;

		if (isNotNil(newValue)) {
			onSelectedItemChange(newValue);
			this._reducer.setValue(newValue.value);
		} else {
			onSelectedItemChange(undefined);
			this._reducer.setValue(this._defaultValue);
		}

		this.validation?.triggerInputFinished();

		for (const handler of this._extraOnChangeHandlers) {
			handler(newValue);
		}
	}

	public onErrorChange(error: ComboBoxControlError | undefined): void {
		this.validation?.triggerInputFinished({
			controlValidation: isNotNil(error)
				? {
					kind: InputValidationResultKind.Invalid,
					errorMessage: error.message
				}
				: {
					kind: InputValidationResultKind.Valid
				}
		});
	}

	private ensureSelectedItemMatchesTheValue(): void {
		const selectedItem: SelectedComboBoxItem<TInputValue> | undefined = this._attachableHandler
			.selectedItem;

		const value: TInputValue = this._reducer
			.value;

		if (!this._areEqual(
			value,
			selectedItem === undefined
				? this._defaultValue
				: selectedItem.value
		)) {
			throw new Error(`Selected item's value does not match form value.
Make sure you call onSelectedItemChange from the ComboBoxIntermediateState **before** updating value in the form data.`);
		}
	}
}